import _ from "lodash";

import defaultClasses from "web/Pages/Checkout/Subscription/BenefitSummary/BenefitForm/benefitForm.scss";
import BenefitFormInput from "web/Pages/Checkout/Subscription/BenefitSummary/BenefitForm/benefitFormInput";
import {
  FORM_RECEIVERS,
  FORM_TYPES,
  newDataIsSavedText,
} from "web/Pages/Checkout/Subscription/BenefitSummary/BenefitForm/constants";
import {
  getInitialFormsValue,
  getNewActiveFormItem,
} from "web/Pages/Customer/Content/Subscriptions/BenefitList/utils";

import sanitizeObject from "web/utils/data/parser/string/sanitizeObject";
import { peselMask } from "web/utils/peselMask";
import BrowserPersistence from "web/utils/system/storage/storage/browserPersistence";

import formLabels from "web/constants/formLabels";
import restUrls from "web/constants/restUrls";
import storageNames from "web/constants/storageNames";

import { SubscriptionFormFieldType } from "web/types/Subscription";

import { request } from "web/api";

const removeSpecificValueFromObject = (obj, valueToRemoved) => {
  const modifiedObject = _.cloneDeep(obj);

  Object.keys(modifiedObject).forEach(
    (k) => modifiedObject[k] === valueToRemoved && delete modifiedObject[k]
  );

  return modifiedObject;
};

export const getFieldName = (key, receiver, formType, id, benefitId) => {
  if (
    receiver === FORM_RECEIVERS.OTHERS &&
    formType === FORM_TYPES.ACTIVE_FORMS
  ) {
    return `${benefitId}.${formType}.${id}.${key}`;
  }
  return `${benefitId}.${formType}.${id}.fields.${key}`;
};

export const getData = (
  fieldsStructured,
  receiver,
  id,
  formType,
  benefitId,
  isCompleted,
  allowEmployeeChangeFields
) => {
  let fieldsToMap = fieldsStructured;
  const dataT1Values = {
    firstname: "firstName",
    lastname: "lastName",
    pesel: "pesel",
    email: "businessEmail",
  };

  if (
    receiver === FORM_RECEIVERS.OTHERS &&
    formType === FORM_TYPES.ACTIVE_FORMS &&
    !isCompleted
  ) {
    fieldsToMap = [{ type: "receiverEmail", label: "E-mail" }];
  }

  if (
    fieldsToMap.some((fieldEle) => fieldEle.type === "receiverEmail") &&
    fieldsToMap.every((fieldEle) => fieldEle.type !== "confirmEmail")
  ) {
    fieldsToMap.forEach((fieldEle, index) => {
      if (fieldEle.type === "receiverEmail") {
        fieldsToMap.splice(index + 1, 0, {
          type: "confirmEmail",
          label: "Powtórz e-mail",
        });
      }
    });
  }

  const getFieldSpecificProps = (type) => {
    switch (type) {
      case SubscriptionFormFieldType.FIRSTNAME:
      case SubscriptionFormFieldType.LASTNAME: {
        const isFieldDisabled =
          receiver === FORM_RECEIVERS.EMPLOYEE &&
          !isCompleted &&
          !allowEmployeeChangeFields;

        return { disabled: isFieldDisabled };
      }
      case SubscriptionFormFieldType.PESEL:
        return { mask: peselMask };
      case SubscriptionFormFieldType.EMAIL:
      case SubscriptionFormFieldType.RECEIVER_EMAIL: {
        return { disablePaste: true };
      }
      default:
        return {};
    }
  };

  return fieldsToMap.map(({ type, label }) => {
    const fieldSpecificProps = getFieldSpecificProps(type);

    return (
      <div className={defaultClasses.field} key={`${id}_${type}`}>
        <BenefitFormInput
          label={label || formLabels[type] || type}
          name={getFieldName(type, receiver, formType, id, benefitId)}
          dataT1={dataT1Values[type]}
          isDataT1NotChanged={!!dataT1Values[type]}
          {...fieldSpecificProps}
        />
      </div>
    );
  });
};

export const updateDataInDraft = (
  benefitUUID,
  newBenefitData,
  formType,
  onSuccessFunction,
  onErrorFunction,
  isMyBenefitsView
) => {
  const storage = new BrowserPersistence();
  const token = storage.getItem(storageNames.tokenAccess);

  const modifiedData = { ...newBenefitData };

  if (newBenefitData.fields) {
    const cleanedFields = removeSpecificValueFromObject(
      newBenefitData.fields,
      ""
    );
    modifiedData.fields = cleanedFields;
  }

  const draftItemsReplacePath =
    restUrls[
      formType === FORM_TYPES.ACTIVE_FORMS
        ? "employeeSubscriptionDraftItemsEditActiveForm"
        : "employeeSubscriptionDraftItemsEditForm"
    ];
  const itemsReplacePath =
    restUrls[
      formType === FORM_TYPES.ACTIVE_FORMS
        ? "employeeSubscriptionItemsEditActiveForm"
        : "employeeSubscriptionItemsEditForm"
    ];
  const path = isMyBenefitsView ? itemsReplacePath : draftItemsReplacePath;

  const prepModifiedData = {
    ...modifiedData,
    fields: sanitizeObject(modifiedData.fields),
  };

  return request(path.replace(":uuid", benefitUUID), {
    headers: {
      accept: "application/ld+json",
      authorization: `Bearer ${token}`,
    },
    method: "POST",
    body: JSON.stringify({
      data: prepModifiedData,
    }),
  })
    .then((response) => {
      if (onSuccessFunction) onSuccessFunction(response);
    })
    .catch((error) => {
      if (onErrorFunction) onErrorFunction();
      console.error(error);
    });
};

export const onActiveFormSubmitSuccess = (
  handleItemUpdate,
  formId,
  newValue,
  setFormsEditedByUsers,
  formUUID,
  setFieldValue,
  field,
  activeFormValues,
  showModalNotification,
  handleToggleEditModal,
  setIsLoading,
  isMyBenefitsView
) => {
  let newActiveFormItem;
  if (handleItemUpdate) {
    handleItemUpdate((currentItem) => {
      newActiveFormItem = getNewActiveFormItem(currentItem, formId, newValue);
      return newActiveFormItem;
    });
  }

  let newFormsEditedByUsers;

  setFormsEditedByUsers((currentList) => {
    const newValue = currentList.includes(formUUID)
      ? currentList
      : [...currentList, formUUID];
    newFormsEditedByUsers = [...newValue];
    return newValue;
  });

  setFieldValue(field, activeFormValues, false);
  showModalNotification(newDataIsSavedText, 2, () =>
    handleDataChangedCallback(
      handleToggleEditModal,
      newActiveFormItem,
      newFormsEditedByUsers
    )
  );
  if (!isMyBenefitsView) setIsLoading(false);
};

export const handleDataChangedCallback = (
  handleToggleEditModal,
  updatedItem,
  newFormsEditedByUsers
) => {
  switch (true) {
    case !handleToggleEditModal:
      return null;
    case !!updatedItem:
      return handleToggleEditModal(
        updatedItem,
        getInitialFormsValue(updatedItem),
        newFormsEditedByUsers
      );
    default:
      return handleToggleEditModal();
  }
};
